import jsPDF from "jspdf";

// BVN Verification Data interface
interface BVNVerificationData {
  bvn: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth?: string;
  phoneNumber1?: string;
  gender?: string;
  image?: string;
  // Advanced fields
  lgaOfOrigin?: string;
  lgaOfResidence?: string;
  nameOnCard?: string;
  phoneNumber2?: string;
  registrationDate?: string;
  residentialAddress?: string;
  stateOfOrigin?: string;
  stateOfResidence?: string;
  enrollmentBank?: string;
  enrollmentBranch?: string;
  levelOfAccount?: string;
  maritalStatus?: string;
  nin?: string;
  title?: string;
  watchListed?: string;
}

export const generateBVNSlipPDF = async (
  data: BVNVerificationData,
  slipType: "basic_slip" | "advanced_slip" = "basic_slip",
  dateGenerated?: string
): Promise<void> => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();

  // Helper function to format date
  const formatDateOfBirth = (dateString?: string) => {
    if (!dateString) return "Not Provided";
    try {
      const date = new Date(dateString);
      return date
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .toUpperCase()
        .replace(",", "");
    } catch {
      return dateString;
    }
  };

  // Helper function to format date generated
  const formatDateGenerated = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original string if invalid date
      }
      return date
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .toUpperCase()
        .replace(",", "");
    } catch {
      return dateString;
    }
  };

  // Helper function to format BVN with proper spacing
  const formatBVN = (bvn: string) => {
    if (!bvn) return "N/A";
    return bvn.replace(/(\d{3})(\d{4})(\d{4})/, "$1 $2 $3");
  };

  // Set up fonts
  doc.setFont("helvetica");

  // No background color

  // Main content container
  const containerWidth = 150;
  const containerHeight = 250;
  const startX = (pageWidth - containerWidth) / 2;
  const startY = 20;

  // White background for content
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(startX, startY, containerWidth, containerHeight, 3, 3, "F");

  // Header
  doc.setFillColor(32, 201, 151); // Primary color
  doc.roundedRect(startX, startY, containerWidth, 15, 3, 3, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(
    "BVN VERIFICATION REPORT",
    startX + containerWidth / 2,
    startY + 10,
    { align: "center" }
  );

  let currentY = startY + 25;

  // Add user photo if available
  if (
    data.image &&
    data.image !==
      "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
  ) {
    try {
      const imageData = data.image.startsWith("data:")
        ? data.image
        : `data:image/jpeg;base64,${data.image}`;

      // Add photo placeholder
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(startX + 10, currentY, 35, 40, 2, 2, "F");

      // Try to add the actual image
      try {
        doc.addImage(imageData, "JPEG", startX + 11, currentY + 1, 33, 38);
      } catch (imageError) {
        // If image fails to load, just show placeholder
        doc.setTextColor(150, 150, 150);
        doc.setFontSize(8);
        doc.text("Photo", startX + 27, currentY + 22, { align: "center" });
      }

      currentY += 50;
    } catch (error) {
      console.warn("Failed to process image:", error);
    }
  }

  // Personal Details Section
  doc.setTextColor(32, 201, 151);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Personal Details", startX + 10, currentY);
  currentY += 8;

  // Table setup
  const tableStartX = startX + 10;
  const tableWidth = containerWidth - 20;
  const rowHeight = 8;
  const labelWidth = 40;

  // Helper function to add table row
  const addTableRow = (label: string, value: string, isAlternate = false) => {
    if (isAlternate) {
      doc.setFillColor(248, 249, 250);
      doc.rect(tableStartX, currentY - 2, tableWidth, rowHeight, "F");
    }

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(label, tableStartX + 2, currentY + 4);

    doc.setFont("helvetica", "normal");
    doc.text(value, tableStartX + labelWidth + 2, currentY + 4);

    currentY += rowHeight + 2; // Add extra spacing between rows
  };

  // Basic information rows
  addTableRow("BVN Number", formatBVN(data.bvn), false);
  addTableRow("First Name", data.firstName || "N/A", true);
  addTableRow("Middle Name", data.middleName || "N/A", false);
  addTableRow("Last Name", data.lastName || "N/A", true);
  addTableRow(
    "Date of Birth",
    data.dateOfBirth ? formatDateOfBirth(data.dateOfBirth) : "N/A",
    false
  );
  addTableRow(
    "Gender",
    data.gender
      ? data.gender.charAt(0).toUpperCase() + data.gender.slice(1)
      : "N/A",
    true
  );
  addTableRow("Phone Number", data.phoneNumber1 || "N/A", false);

  // Advanced fields - only show for advanced slip
  if (slipType === "advanced_slip") {
    if (data.nameOnCard) {
      addTableRow("Name on Card", data.nameOnCard, true);
    }
    if (data.phoneNumber2) {
      addTableRow("Phone Number 2", data.phoneNumber2, false);
    }
    if (data.residentialAddress) {
      addTableRow("Residential Address", data.residentialAddress, true);
    }
    if (data.stateOfOrigin) {
      addTableRow("State of Origin", data.stateOfOrigin, false);
    }
    if (data.lgaOfOrigin) {
      addTableRow("LGA of Origin", data.lgaOfOrigin, true);
    }
    if (data.stateOfResidence) {
      addTableRow("State of Residence", data.stateOfResidence, false);
    }
    if (data.lgaOfResidence) {
      addTableRow("LGA of Residence", data.lgaOfResidence, true);
    }
    if (data.registrationDate) {
      addTableRow("Registration Date", data.registrationDate, false);
    }
    if (data.enrollmentBank) {
      addTableRow("Enrollment Bank", data.enrollmentBank, true);
    }
    if (data.enrollmentBranch) {
      addTableRow("Enrollment Branch", data.enrollmentBranch, false);
    }
    if (data.levelOfAccount) {
      addTableRow("Level of Account", data.levelOfAccount, true);
    }
    if (data.maritalStatus) {
      addTableRow("Marital Status", data.maritalStatus, false);
    }
    if (data.title) {
      addTableRow("Title", data.title, true);
    }
    if (data.nin) {
      addTableRow("NIN", data.nin, false);
    }
    if (data.watchListed) {
      addTableRow("Watch Listed", data.watchListed, true);
    }
  }

  // Transaction details
  addTableRow("Date Generated", formatDateGenerated(dateGenerated), true);

  // Footer
  currentY += 10;
  doc.setDrawColor(32, 201, 151);
  doc.line(startX + 10, currentY, startX + containerWidth - 10, currentY);

  // Download the PDF
  const fileName = `bvn-verification-${data.bvn}-${
    new Date().toISOString().split("T")[0]
  }.pdf`;
  doc.save(fileName);
};
